import { depascalize } from "sprint-common-web";

import {
    GpacMuxSpecificationListingItemResponse,
    ManzanitaMuxSpecificationListingItemResponse,
    MuxSpecificationListingItemResponse
} from "../../../common/api/orders-api/mux-specification/responses/GetMuxSpecificationListResponse";
import { mapNumberToMuxerType, MuxerType } from "../../../common/types/MuxerType";
import { mapToEncodeSpecificationType } from "../../../common/utils/renderEncodeSpecificationType";
import { LanguageSpecificationType } from "../../common/types/LanguageSpecificationType";
import { EncodeSpecificationType } from "../../encode-specification/context/EncodeSpecificationItem";

export interface MuxSpecificationListingItem {
    id: string;
    name: string;
    type: MuxerType;
    encodeSpecificationType: EncodeSpecificationType;
    details: MuxDetails;
    updatedDate: string;
    isModified?: boolean;
}

export type MuxDetails = GpacMuxDetails | ManzanitaMuxDetails;

export interface GpacMuxDetails {
    type: MuxerType.GPAC;
    command: string;
    soundtracksCommand: string;
    subtitlesCommand?: string;
}

export interface ManzanitaMuxDetails {
    type: MuxerType.Manzanita;
    vbr?: string;
    autoDescrAVC?: string;
    pcrPID: string;
    pmtPID: string;
    videoPID: string;
    pesAlign?: string;
    maxDecodeDelay?: string;
    languageSpecificationType: string;
}

export const mapToMuxSpecificationListingItem = (
    muxSpecification: MuxSpecificationListingItemResponse
): MuxSpecificationListingItem => {
    const muxerType = mapNumberToMuxerType(muxSpecification.muxerType);

    return {
        details: mapToDetails(muxSpecification),
        type: muxerType,
        encodeSpecificationType: mapToEncodeSpecificationType(muxSpecification.encodeSpecificationType),
        name: muxSpecification.name,
        id: muxSpecification.id,
        updatedDate: muxSpecification.updatedDate
    };
};

const convertNullableBooleanToNullableString = (value: boolean | undefined) =>
    value !== undefined ? convertBooleanToString(value) : undefined;

const convertBooleanToString = (value: boolean) => (value ? "yes" : "no");

const mapToDetails = (response: MuxSpecificationListingItemResponse) => {
    switch (response.muxerType) {
        case MuxerType.Manzanita:
            return mapToManzanitaMuxSpecificationDetails(response);
        case MuxerType.GPAC:
            return mapToGpacMuxSpecificationDetails(response);
        default: {
            throw new Error("Unsupported Muxer Type");
        }
    }
};

const mapToManzanitaMuxSpecificationDetails = (
    response: ManzanitaMuxSpecificationListingItemResponse
): ManzanitaMuxDetails => {
    return {
        type: MuxerType.Manzanita,
        vbr: convertNullableBooleanToNullableString(response.vbr),
        autoDescrAVC: convertNullableBooleanToNullableString(response.autoDescrAvc),
        pcrPID: response.pcrPid,
        pmtPID: response.pmtPid,
        videoPID: response.videoPid,
        pesAlign: convertNullableBooleanToNullableString(response.pesAlign),
        maxDecodeDelay: response.maxDecodeDelay?.toString(),
        languageSpecificationType: depascalize(LanguageSpecificationType[response.languageSpecificationType])
    };
};

const mapToGpacMuxSpecificationDetails = (response: GpacMuxSpecificationListingItemResponse): GpacMuxDetails => {
    return {
        type: MuxerType.GPAC,
        command: response.command,
        soundtracksCommand: response.soundtracksCommand,
        subtitlesCommand: response.subtitlesCommand
    };
};
